import {
  AddOnSchema,
  BaySchema,
  DailyStatsSchema,
  PackageSchema,
  ProductSchema,
  ProductSalesConfigSchema,
  PromotionApplicationSchema,
  ServiceSchema,
  StaffDocSchema,
  TicketSchema,
  VehicleTypeSchema,
} from '@kilat/shared';
import { z } from 'zod';
import { createRepo } from './_base.js';

/**
 * Outlet-scoped repo factories for the dashboard.
 *
 * Sprint 53: added the four catalog collections (services / packages /
 * addOns / vehicleTypes) for the new `/catalog` editor.
 */

const OutletDocSchema = z.object({
  // The repo layer injects the Firestore doc id as `{ id: d.id, ...data }`
  // before parsing (see `_base.ts` list/listSnapshot/get). Zod's default
  // `z.object` STRIPS any key it doesn't declare, so without this field the
  // id was silently dropped — `useActiveOutlet` then resolved `outletId` to
  // `null` and every outlet-scoped route (queue/pos/staff/reports/qr-print)
  // dead-ended at the "pick an outlet" gate with no way forward. Declaring
  // `id` here lets the injected value survive the parse. Optional because
  // writes via `.set()` pass a value object without an id (id is never a
  // stored field). Found by the dashboard e2e harness (slice 2), 2026-07-17.
  id: z.string().optional(),
  profile: z.unknown(),
  catalogSettings: z.unknown().optional(),
  queueSettings: z.unknown().optional(),
});

export function outletDocRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets`,
    schema: OutletDocSchema,
    name: `tenants/${tenantId}/outlets`,
  });
}

export function ticketsRepoFor(tenantId: string, outletId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets/${outletId}/queue`,
    schema: TicketSchema,
    name: `outlets/${outletId}/queue`,
  });
}

/**
 * `tenants/{tid}/outlets/{oid}/bays/{bayId}` — named wash bays for the queue
 * board's bay picker (roadmap #13 slice B). Read = any tenant staff, write =
 * owner/manager (enforced by `infra/firestore.rules` — no callable needed for
 * config). Deactivating a bay is a soft-delete (`active:false`) so historical
 * `ticket.bayId` references stay resolvable.
 */
export function baysRepoFor(tenantId: string, outletId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets/${outletId}/bays`,
    schema: BaySchema,
    name: `outlets/${outletId}/bays`,
  });
}

// ── catalog sub-collections (Sprint 53) ────────────────────────────────
//
// Sprint 104 — Services + AddOns migrated to tenant-library
// (`tenants/{tid}/services/{id}` + `tenants/{tid}/addOns/{id}`). Caller
// passes the outlet through `filterForOutlet` (from @kilat/shared) to
// compute per-outlet visibility via each item's `excludedOutletIds`.
// See [[project-tenant-library-catalog]] + `docs/architecture/tenant-library.md`.
//
// Packages + vehicleTypes are still per-outlet — they migrate in
// Sprints 105 + 106 respectively. Don't unify yet.

export function servicesRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/services`,
    schema: ServiceSchema,
    name: `tenants/${tenantId}/services`,
  });
}

// Sprint 105 — Packages migrated to tenant-library. Callers use
// `filterForOutlet` (from @kilat/shared) to scope by `excludedOutletIds`
// and `effectivePackagePrice` to apply per-outlet `outletPriceOverrides`.
// See `docs/architecture/tenant-library.md`.
export function packagesRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/packages`,
    schema: PackageSchema,
    name: `tenants/${tenantId}/packages`,
  });
}

export function addOnsRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/addOns`,
    schema: AddOnSchema,
    name: `tenants/${tenantId}/addOns`,
  });
}

// Product cross-sell (Pro). Tenant-library items sold alongside a wash
// (`tenants/{tid}/products/{id}`), flat-priced, `excludedOutletIds` per-outlet
// visibility. The `/products` editor writes here; bookTicket /
// appendItemsToTicket price them server-side once the two-condition gate
// passes (plan entitlement + the opt-in below).
export function productsRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/products`,
    schema: ProductSchema,
    name: `tenants/${tenantId}/products`,
  });
}

/** `tenants/{tid}/productSales/config` — the operator opt-in toggle (single
 *  doc, id = 'config'). Same shape as loyaltyConfigRepoFor: a collection repo
 *  whose only doc is `config`. Missing = disabled (off by default). */
export function productSalesConfigRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/productSales`,
    schema: ProductSalesConfigSchema,
    name: `tenants/${tenantId}/productSales`,
  });
}

/** Doc id of the single product-sales config document. */
export const PRODUCT_SALES_CONFIG_DOC_ID = 'config';

// Sprint 106 — VehicleTypes migrated to tenant-library. Callers use
// `filterForOutlet` (from @kilat/shared) to scope by `excludedOutletIds`.
// Closes the tenant-library migration. See
// `docs/architecture/tenant-library.md`.
export function vehicleTypesRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/vehicleTypes`,
    schema: VehicleTypeSchema,
    name: `tenants/${tenantId}/vehicleTypes`,
  });
}

/**
 * `tenants/{tid}/outlets/{oid}/ticketsHistory/{ticketId}` — archived
 * tickets written by the `recordPayment` Cloud Function when a ticket
 * moves to `collected` status. Carries the full ticket shape including
 * `payment.method` + `payment.amount` + `payment.receivedAt` plus
 * `snapshot.packageId` + `snapshot.total` — the source for the
 * `/reports` page's KPIs + breakdowns (Sprint 54).
 */
export function ticketsHistoryRepoFor(tenantId: string, outletId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets/${outletId}/ticketsHistory`,
    schema: TicketSchema,
    name: `outlets/${outletId}/ticketsHistory`,
  });
}

/**
 * `tenants/{tid}/outlets/{oid}/stats/{YYYY-MM-DD}` — daily rollup docs written
 * by the `aggregationsDaily` Cloud Function (audit 2026-07-05 §2 P1). Each doc
 * carries the day's ticket count, gross revenue sen, and per-method /
 * per-package breakdowns. `/reports` range-reads these (≤ ~34 docs) instead of
 * scanning `ticketsHistory`. Missing day = zero day (idle outlets write nothing).
 */
export function statsRepoFor(tenantId: string, outletId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets/${outletId}/stats`,
    schema: DailyStatsSchema,
    name: `outlets/${outletId}/stats`,
  });
}

/**
 * `tenants/{tid}/outlets/{oid}/promotionApplications/{ticketId}_{promotionId}` —
 * append-only audit trail of every promotion applied to a ticket, written by
 * the `bookTicket` + `appendItemsToTicket` Cloud Functions (admin SDK) in the
 * same transaction that stamps the ticket. Read = owner/manager/cashier
 * (`infra/firestore.rules`). The `/loyalty/promotions` page reads a BOUNDED
 * slice (latest N by `appliedAt`) via `listSnapshotBounded` — never an
 * unbounded scan, since this collection grows with every discounted booking.
 */
export function promotionApplicationsRepoFor(tenantId: string, outletId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets/${outletId}/promotionApplications`,
    schema: PromotionApplicationSchema,
    name: `outlets/${outletId}/promotionApplications`,
  });
}

/**
 * `tenants/{tid}/outlets/{oid}/staff/{uid}` — per-outlet staff
 * roster. Doc id IS the user's Firebase Auth uid. The Cloud Function
 * `claimsSyncStaff` watches this collection and aggregates roles
 * across all of a tenant's outlets into the user's `claims.roles[tid]`.
 * Sprint 55 — drives the new `/staff` page.
 */
export function staffRepoFor(tenantId: string, outletId: string) {
  return createRepo({
    path: `tenants/${tenantId}/outlets/${outletId}/staff`,
    schema: StaffDocSchema,
    name: `outlets/${outletId}/staff`,
  });
}
